// Shared pure helpers for the student Communities/Chat MVP. No API calls here —
// components own their own requests via the shared axios instance, matching the
// rest of the app's Discovery/Support pages.

export const MESSAGE_MAX_LENGTH = 2000;

// crypto.randomUUID() isn't available in every WebView/older browser context
// Capacitor may run in, so fall back to a small dependency-free generator
// rather than pulling in a uuid package.
const fallbackUuid = () => {
    const now = Date.now();
    const rand = Math.floor(Math.random() * 1e9);
    return `cm-${now.toString(36)}-${rand.toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
};

export const generateClientMessageId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return fallbackUuid();
};

export const sortMessagesAsc = (messages = []) =>
    [...messages].sort((a, b) => {
        const timeDiff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        if (timeDiff !== 0) return timeDiff;
        return String(a._id).localeCompare(String(b._id));
    });

// A message and its optimistic (locally-created, not-yet-confirmed) placeholder
// share the same clientMessageId, so keying on it lets a confirmed message
// (from a send response or a poll) transparently replace its placeholder.
const messageMergeKey = (message) =>
    message.clientMessageId ? `c:${message.clientMessageId}` : `i:${message._id}`;

// Merges any two message lists (older page + current, current + poll result,
// optimistic placeholder + confirmed message, ...), de-duplicating and
// re-sorting chronologically. Later entries in `incoming` win over `existing`.
export const mergeMessages = (existing = [], incoming = []) => {
    const map = new Map();
    [...existing, ...incoming].forEach((message) => {
        if (!message) return;
        map.set(messageMergeKey(message), message);
    });
    return sortMessagesAsc(Array.from(map.values()));
};

export const removeMessageByClientId = (messages = [], clientMessageId) =>
    messages.filter((message) => message.clientMessageId !== clientMessageId);

export const getMessageSenderName = (sender) => {
    const name = [sender?.firstName, sender?.familyName].filter(Boolean).join(' ').trim();
    return name || 'Student';
};

export const getCommunityDisplayName = (conversation) => {
    if (!conversation) return '';
    if (conversation.type === 'city') return conversation.city?.name || 'City community';
    if (conversation.type === 'country') return conversation.country?.country?.name || 'Country community';
    return 'Community';
};

export const getCommunitySubtitle = (conversation) => {
    if (!conversation) return '';
    if (conversation.type === 'city') return conversation.city?.country?.name || '';
    if (conversation.type === 'country') return conversation.country?.continent || '';
    return '';
};

const pad2 = (n) => String(n).padStart(2, '0');

const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

// Short, chat-list-appropriate timestamp: "14:32" today, "Mon" this week, else a short date.
export const formatConversationTimestamp = (iso) => {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const now = new Date();

    if (isSameDay(date, now)) return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;

    const daysDiff = Math.floor((now.setHours(0, 0, 0, 0) - new Date(date).setHours(0, 0, 0, 0)) / 86400000);
    if (daysDiff >= 0 && daysDiff < 7) {
        return date.toLocaleDateString('en-GB', { weekday: 'short' });
    }
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

// Full time for message bubbles within the conversation.
export const formatMessageTime = (iso) => {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

export const formatUnreadBadge = (count) => {
    const n = Number(count) || 0;
    if (n <= 0) return '';
    return n > 99 ? '99+' : String(n);
};

// Last-message preview line for the Communities list (desktop table + mobile card).
export const getCommunityPreviewText = (conversation, currentUserId) => {
    const lastMessage = conversation?.lastMessage;
    if (!lastMessage) return 'No messages yet';
    if (lastMessage.isDeleted) return 'Message removed';
    if (lastMessage.isHidden) return 'Message unavailable';
    if (!lastMessage.text) return 'No messages yet';
    const isMine = String(lastMessage.sender?._id || lastMessage.sender) === String(currentUserId);
    return `${isMine ? 'You: ' : ''}${lastMessage.text}`;
};
